/**
 * GameMenu - Unified menu system for all game menus
 *
 * All menus (Inventory, Skills, Passives, Craft, Map, Save) are tabs in one panel.
 * Keyboard shortcuts open the menu with that tab active.
 */

import { Renderer } from '@client/core/Renderer';
import { Player } from '@client/entities/Player';
import { AssetLoader } from '@client/core/AssetLoader';
import { getAudioManager } from '@client/audio/AudioManager';
import {
  Panel,
  TabContainer,
  TabDefinition,
  Rect,
} from './components';
import { MenuTabContent } from './MenuTabContent';
import {
  InventoryTab,
  SkillsTabNew,
  PassivesTab,
  CraftTab,
  MapTab,
  SaveTab,
  QuestsTab,
  EditorTab,
} from './tabs';
import { debugLog } from '@shared/utils/Logger';
import { t } from '@locales/index';

// Re-export MenuTabContent for backwards compatibility
export { MenuTabContent } from './MenuTabContent';

// Tab IDs
export type MenuTabId = 'inventory' | 'skills' | 'passives' | 'craft' | 'quests' | 'map' | 'editor' | 'save';

// Tab configuration
interface MenuTab {
  id: MenuTabId;
  label: string;
  shortcut: string;
}

const TAB_LOCALE_KEYS: Record<MenuTabId, string> = {
  inventory: 'gameMenu.inventory',
  skills: 'gameMenu.skills',
  passives: 'gameMenu.passives',
  craft: 'gameMenu.craft',
  quests: 'gameMenu.quests',
  map: 'gameMenu.map',
  editor: 'gameMenu.editor',
  save: 'gameMenu.save',
};

const TAB_SHORTCUTS: Record<MenuTabId, string> = {
  inventory: 'I',
  skills: 'K',
  passives: 'P',
  craft: 'C',
  quests: 'J',
  map: 'M',
  editor: 'F6',
  save: 'F5',
};

const TAB_IDS: MenuTabId[] = ['inventory', 'skills', 'passives', 'craft', 'quests', 'map', 'editor', 'save'];

function getMenuTabs(): MenuTab[] {
  return TAB_IDS.map(id => ({
    id,
    label: t(TAB_LOCALE_KEYS[id]),
    shortcut: TAB_SHORTCUTS[id],
  }));
}

/**
 * Main GameMenu class
 */
export class GameMenu {
  private player: Player;
  private assetLoader: AssetLoader;
  private screenWidth: number;
  private screenHeight: number;

  // State
  private isOpen: boolean = false;
  private fadeIn: number = 0;

  // UI Components
  private panel: Panel;
  private tabContainer: TabContainer;
  private tabContents: Map<MenuTabId, MenuTabContent> = new Map();

  constructor(player: Player, screenWidth: number, screenHeight: number, assetLoader?: AssetLoader) {
    this.player = player;
    this.assetLoader = assetLoader || new AssetLoader();
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create fullscreen panel (no close button)
    this.panel = new Panel({
      title: 'Menü',
      width: screenWidth,
      height: screenHeight,
      showCloseButton: false,
      padding: 0,
    });

    // Create tab container (fullscreen)
    this.tabContainer = new TabContainer({
      width: screenWidth,
      height: screenHeight - 50, // Account for panel title
      tabBarHeight: 45,
      contentPadding: 15,
    });

    // Initialize tab contents (placeholders for now)
    this.initializeTabContents();

    // Set up tabs
    const tabDefs: TabDefinition[] = getMenuTabs().map(tab => ({
      id: tab.id,
      label: tab.label,
      shortcut: tab.shortcut,
      content: this.tabContents.get(tab.id)!,
    }));

    this.tabContainer.setTabs(tabDefs);

    // Add tab container to panel
    this.panel.addChild(this.tabContainer);
  }

  private initializeTabContents(): void {
    // Create real tab contents
    this.tabContents.set('inventory', new InventoryTab(this.player, this.assetLoader));
    this.tabContents.set('skills', new SkillsTabNew(this.player));
    this.tabContents.set('passives', new PassivesTab(this.player));
    this.tabContents.set('craft', new CraftTab(this.player));
    this.tabContents.set('quests', new QuestsTab(this.player));
    this.tabContents.set('map', new MapTab(this.player));
    this.tabContents.set('editor', new EditorTab(this.player));

    const saveTab = new SaveTab(this.player);
    saveTab.onSave = (slot) => {
      // Will be connected by Game.ts
      debugLog('GameMenu', `Save to slot ${slot}`);
    };
    saveTab.onLoad = (slot) => {
      // Will be connected by Game.ts
      debugLog('GameMenu', `Load from slot ${slot}`);
    };
    this.tabContents.set('save', saveTab);
  }

  /**
   * Get QuestsTab instance
   */
  getQuestsTab(): QuestsTab | undefined {
    return this.tabContents.get('quests') as QuestsTab | undefined;
  }

  /**
   * Get EditorTab instance
   */
  getEditorTab(): EditorTab | undefined {
    return this.tabContents.get('editor') as EditorTab | undefined;
  }

  /**
   * Get MapTab instance
   */
  getMapTab(): MapTab | undefined {
    return this.tabContents.get('map') as MapTab | undefined;
  }

  /**
   * Set save/load callbacks for SaveTab
   */
  setSaveCallbacks(onSave: (slot: number) => void, onLoad: (slot: number) => void): void {
    const saveTab = this.tabContents.get('save') as SaveTab;
    if (saveTab) {
      saveTab.onSave = onSave;
      saveTab.onLoad = onLoad;
    }
  }

  /**
   * Register a real tab content implementation
   */
  registerTabContent(tabId: MenuTabId, content: MenuTabContent): void {
    this.tabContents.set(tabId, content);

    // Update tab container
    const tabDefs: TabDefinition[] = getMenuTabs().map(tab => ({
      id: tab.id,
      label: tab.label,
      shortcut: tab.shortcut,
      content: this.tabContents.get(tab.id)!,
    }));

    this.tabContainer.setTabs(tabDefs);
  }

  private updatePanelSize(): void {
    // Update panel and tab container to match screen size
    this.panel = new Panel({
      title: 'Menü',
      width: this.screenWidth,
      height: this.screenHeight,
      showCloseButton: false,
      padding: 0,
    });

    this.tabContainer = new TabContainer({
      width: this.screenWidth,
      height: this.screenHeight - 50,
      tabBarHeight: 45,
      contentPadding: 15,
    });

    // Re-setup tabs
    const tabDefs: TabDefinition[] = getMenuTabs().map(tab => ({
      id: tab.id,
      label: tab.label,
      shortcut: tab.shortcut,
      content: this.tabContents.get(tab.id)!,
    }));
    this.tabContainer.setTabs(tabDefs);
    this.panel.addChild(this.tabContainer);
  }

  // Open/Close methods
  open(tabId?: MenuTabId): void {
    this.isOpen = true;
    this.fadeIn = 0;
    getAudioManager().play('ui_open');

    if (tabId) {
      this.tabContainer.setActiveTab(tabId);
    }

    // Notify active tab
    const activeContent = this.tabContents.get(this.tabContainer.getActiveTabId() as MenuTabId);
    if (activeContent) {
      activeContent.onActivate();
    }
  }

  close(): void {
    // Notify active tab
    const activeContent = this.tabContents.get(this.tabContainer.getActiveTabId() as MenuTabId);
    if (activeContent) {
      activeContent.onDeactivate();
    }

    this.isOpen = false;
    getAudioManager().play('ui_close');
  }

  toggle(tabId?: MenuTabId): void {
    if (this.isOpen) {
      // If same tab, close. If different tab, switch to it.
      if (tabId && tabId !== this.tabContainer.getActiveTabId()) {
        this.tabContainer.setActiveTab(tabId);
      } else {
        this.close();
      }
    } else {
      this.open(tabId);
    }
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  getActiveTabId(): MenuTabId {
    return this.tabContainer.getActiveTabId() as MenuTabId;
  }

  // Handle keyboard shortcut - returns tab ID if matched
  handleShortcut(key: string): MenuTabId | null {
    const upperKey = key.toUpperCase();
    const tab = getMenuTabs().find(t => t.shortcut.toUpperCase() === upperKey);
    return tab?.id || null;
  }

  // Update
  update(deltaTime: number): void {
    if (!this.isOpen) return;

    // Fade in animation
    if (this.fadeIn < 1) {
      this.fadeIn = Math.min(1, this.fadeIn + deltaTime * 4);
    }

    // Update active tab content
    const activeContent = this.tabContents.get(this.tabContainer.getActiveTabId() as MenuTabId);
    if (activeContent) {
      activeContent.update(deltaTime);
    }
  }

  // Render
  render(renderer: Renderer): void {
    if (!this.isOpen) return;

    const ctx = renderer.context;
    const alpha = this.fadeIn;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Layout panel (fullscreen)
    const panelRect: Rect = {
      x: 0,
      y: 0,
      width: this.screenWidth,
      height: this.screenHeight,
    };
    this.panel.layout(ctx, panelRect);

    // Render panel
    this.panel.render(ctx);

    ctx.restore();
  }

  // Mouse handling
  handleMouseMove(mouseX: number, mouseY: number): void {
    if (!this.isOpen) return;

    this.panel.handleMouseEvent({
      x: mouseX,
      y: mouseY,
      button: 'left',
      type: 'move',
    });
  }

  handleMouseDown(mouseX: number, mouseY: number, button: 'left' | 'right' | 'middle' = 'left', shiftKey: boolean = false): boolean {
    if (!this.isOpen) return false;

    return this.panel.handleMouseEvent({
      x: mouseX,
      y: mouseY,
      button,
      type: 'down',
      shiftKey,
    });
  }

  handleMouseUp(mouseX: number, mouseY: number): void {
    if (!this.isOpen) return;

    this.panel.handleMouseEvent({
      x: mouseX,
      y: mouseY,
      button: 'left',
      type: 'up',
    });
  }

  handleWheel(mouseX: number, mouseY: number, deltaY: number): void {
    if (!this.isOpen) return;

    this.panel.handleMouseEvent({
      x: mouseX,
      y: mouseY,
      button: 'left',
      type: 'wheel',
      deltaY,
    });
  }

  // Resize handling
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.updatePanelSize();
  }
}
